import { useQuery, useMutation } from "@tanstack/react-query";
import { SessionVerification, VerificationStep } from "@/types/verificationTypes";
import { useToast } from "@/hooks/use-toast";
import {
  getSessions,
  getStepsBySessionId,
  purgeOldSessions,
  cleanupIncompleteTestSessions,
  isVerificationTrackingEnabled,
  deleteAllSessions
} from "@/services/VerificationService";

export const useVerificationData = (autoRefresh = false) => {
  const { toast } = useToast();
  
  // Try to get query client safely - we'll handle the error at the component level instead
  const isTrackingEnabled = isVerificationTrackingEnabled();

  // Get sessions with filtering
  const useSessionsQuery = (statusFilter: string = "all", labelFilter?: string, enabled = true) => {
    return useQuery({
      queryKey: ["verification-sessions", statusFilter, labelFilter],
      queryFn: async () => {
        try {
          const result = await getSessions(statusFilter, labelFilter);
          return result;
        } catch (error: any) {
          console.error("[useVerificationData] Error in fetchSessions:", error);
          throw error;
        }
      },
      refetchInterval: autoRefresh && isTrackingEnabled ? 5000 : false,
      enabled: isTrackingEnabled && enabled,
    });
  };

  // Get steps for a specific session
  const useSessionStepsQuery = (sessionId: string | null) => {
    return useQuery({
      queryKey: ["verification-steps", sessionId],
      queryFn: async () => {
        if (!sessionId) return [];
        
        try {
          return await getStepsBySessionId(sessionId);
        } catch (error: any) {
          console.error("[useVerificationData] Error in fetchStepsForSession:", error);
          throw error;
        }
      },
      enabled: isTrackingEnabled && !!sessionId,
      refetchInterval: autoRefresh && isTrackingEnabled && sessionId ? 5000 : false,
    });
  };

  // Clear old sessions
  const clearOldSessions = useMutation({
    mutationFn: async (daysToKeep: number = 7) => {
      if (!isTrackingEnabled) return false;
      return await purgeOldSessions(daysToKeep);
    },
    onSuccess: () => {
      toast({
        title: "Old sessions cleared",
        description: "Verification sessions older than the specified days have been removed."
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error clearing old sessions",
        description: error.message || "An unexpected error occurred",
      });
    }
  });

  // Clear incomplete test sessions
  const clearIncompleteTestSessions = useMutation({
    mutationFn: async (hoursOld: number = 24) => {
      if (!isTrackingEnabled) return false;
      return await cleanupIncompleteTestSessions(hoursOld);
    },
    onSuccess: () => {
      toast({
        title: "Incomplete sessions cleared",
        description: "Incomplete verification sessions have been cleaned up."
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error clearing incomplete sessions",
        description: error.message || "An unexpected error occurred",
      });
    }
  });

  // Clear all sessions
  const clearAllSessions = useMutation({
    mutationFn: async () => {
      if (!isTrackingEnabled) return false;
      return await deleteAllSessions();
    },
    onSuccess: () => {
      toast({
        title: "All sessions cleared",
        description: "All verification sessions have been removed."
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error clearing all sessions",
        description: error.message || "An unexpected error occurred",
      });
    }
  });

  return {
    useSessionsQuery,
    useSessionStepsQuery,
    clearOldSessions,
    clearIncompleteTestSessions,
    clearAllSessions,
    isTrackingEnabled
  };
};